import React, { createContext, useState, useContext, useEffect } from 'react';
import { message } from 'antd';
import storageService from '../services/storageService';
import { logActivity } from '../services/auditService';
import { ACTIVITY_TYPES } from '../models/activityTypes';
import { 
  createSubscriber, 
  createMeter, 
  createReading, 
  createInvoice,
  createPayment 
} from '../models/types';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [subscribers, setSubscribers] = useState([]);
  const [meters, setMeters] = useState([]);
  const [readings, setReadings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize data from storage
  useEffect(() => {
    const loadData = async () => {
      try {
        const [subs, meters, readings, invoices, payments, tariffs] = await Promise.all([
          storageService.getSubscribers(),
          storageService.getMeters(),
          storageService.getReadings(),
          storageService.getInvoices(),
          storageService.getPayments(),
          storageService.getTariffs()
        ]);
        
        setSubscribers(subs);
        setMeters(meters);
        setReadings(readings);
        setInvoices(invoices);
        setPayments(payments);
        setTariffs(tariffs);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Subscriber operations
  const addSubscriber = async (subscriberData, userId = null) => {
    const subscriber = createSubscriber(subscriberData);
    const result = await storageService.saveSubscriber(subscriber);
    
    if (result.success) {
      setSubscribers([...subscribers, subscriber]);
      message.success('Abonné enregistré avec succès');
      
      // Enregistrer l'activité
      if (userId) {
        try {
          await logActivity(
            ACTIVITY_TYPES.SUBSCRIBER_CREATED,
            userId,
            'subscriber',
            subscriber.id,
            { subscriberData },
            `Abonné créé: ${subscriber.firstName} ${subscriber.lastName}`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la création de l\'abonné:', auditError);
        }
      }
      
      return { success: true, data: subscriber };
    } else {
      message.error(`Erreur lors de l'enregistrement: ${result.error?.message || 'Erreur inconnue'}`);
      console.error('Détails de l\'erreur:', result.error);
      return { success: false, data: subscriber, error: result.error };
    }
  };

  const updateSubscriber = async (id, subscriberData, userId = null) => {
    const oldSubscriber = getSubscriber(id);
    const subscriber = { ...oldSubscriber, ...subscriberData, id };
    const result = await storageService.saveSubscriber(subscriber);
    
    if (result.success) {
      setSubscribers(subscribers.map(s => s.id === id ? subscriber : s));
      message.success('Abonné modifié avec succès');
      
      // Enregistrer l'activité
      if (userId) {
        try {
          const changes = {};
          Object.keys(subscriberData).forEach(key => {
            if (oldSubscriber[key] !== subscriberData[key]) {
              changes[key] = {
                before: oldSubscriber[key],
                after: subscriberData[key]
              };
            }
          });
          
          await logActivity(
            ACTIVITY_TYPES.SUBSCRIBER_UPDATED,
            userId,
            'subscriber',
            id,
            { changes },
            `Abonné modifié: ${subscriber.firstName} ${subscriber.lastName}`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la modification de l\'abonné:', auditError);
        }
      }
      
      return { success: true, data: subscriber };
    } else {
      message.error(`Erreur lors de la modification: ${result.error?.message || 'Erreur inconnue'}`);
      console.error('Détails de l\'erreur:', result.error);
      return { success: false, data: subscriber, error: result.error };
    }
  };

  const getSubscriber = (id) => {
    return subscribers.find(s => s.id === id);
  };

  const deleteSubscriber = async (id, userId = null) => {
    const subscriber = getSubscriber(id);
    await storageService.deleteSubscriber(id);
    setSubscribers(subscribers.filter(s => s.id !== id));
    
    // Enregistrer l'activité
    if (userId && subscriber) {
      try {
        await logActivity(
          ACTIVITY_TYPES.SUBSCRIBER_DELETED,
          userId,
          'subscriber',
          id,
          { deletedName: `${subscriber.firstName} ${subscriber.lastName}` },
          `Abonné supprimé: ${subscriber.firstName} ${subscriber.lastName}`
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la suppression de l\'abonné:', auditError);
      }
    }
  };

  // Meter operations
  const addMeter = async (meterData, userId = null) => {
    const meter = createMeter(meterData);
    const result = await storageService.saveMeter(meter);
    
    if (!result.success) {
      message.error(`Erreur lors de l'ajout du compteur: ${result.error?.message || 'Erreur inconnue'}`);
      console.error('Détails de l\'erreur:', result.error);
      return { success: false, data: meter, error: result.error };
    }
    
    setMeters([...meters, meter]);
    message.success('Compteur enregistré avec succès');
    
    // Enregistrer l'activité
    if (userId) {
      try {
        await logActivity(
          ACTIVITY_TYPES.METER_CREATED,
          userId,
          'meter',
          meter.id,
          { meterData, serialNumber: meter.serialNumber },
          `Compteur créé: ${meter.serialNumber}`
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la création du compteur:', auditError);
      }
    }
    
    // Create initial reading for the meter
    if (meterData.initialReading !== undefined && meterData.initialReading !== null) {
      const initialReadingValue = meterData.initialReading;
      
      // Create only one initial reading with installation date
      const initialReading = {
        ...createReading({
          meterId: meter.id,
          subscriberId: meter.subscriberId,
          meterReading: initialReadingValue,
          readingDate: meter.installationDate,
          notes: 'Lecture initiale'
        }),
        isInitial: true
      };
      await storageService.saveReading(initialReading);
      
      setReadings([...readings, initialReading]);
    }
    
    return { success: true, data: meter };
  };

  const updateMeter = async (id, meterData, userId = null) => {
    const oldMeter = getMeter(id);
    const meter = { ...oldMeter, ...meterData, id };
    const result = await storageService.saveMeter(meter);
    
    if (result.success) {
      setMeters(meters.map(m => m.id === id ? meter : m));
      message.success('Compteur modifié avec succès');
      
      // Enregistrer l'activité
      if (userId) {
        try {
          const changes = {};
          Object.keys(meterData).forEach(key => {
            if (oldMeter[key] !== meterData[key]) {
              changes[key] = {
                before: oldMeter[key],
                after: meterData[key]
              };
            }
          });
          
          await logActivity(
            ACTIVITY_TYPES.METER_UPDATED,
            userId,
            'meter',
            id,
            { changes },
            `Compteur modifié: ${meter.serialNumber}`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la modification du compteur:', auditError);
        }
      }
      
      return { success: true, data: meter };
    } else {
      message.error(`Erreur lors de la modification: ${result.error?.message || 'Erreur inconnue'}`);
      console.error('Détails de l\'erreur:', result.error);
      return { success: false, data: meter, error: result.error };
    }
  };

  const getMeter = (id) => {
    return meters.find(m => m.id === id);
  };

  const getMetersBySubscriber = (subscriberId) => {
    return meters.filter(m => m.subscriberId === subscriberId);
  };

  // Reading operations
  const addReading = async (readingData, userId = null) => {
    const reading = createReading(readingData);
    await storageService.saveReading(reading);
    setReadings([...readings, reading]);
    
    // Enregistrer l'activité
    if (userId) {
      try {
        await logActivity(
          ACTIVITY_TYPES.READING_CREATED,
          userId,
          'reading',
          reading.id,
          { readingData },
          `Relevé créé: ${reading.readingValue} m³`
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la création du relevé:', auditError);
      }
    }
    
    return reading;
  };

  const getReadingsByMeter = (meterId) => {
    return readings.filter(r => r.meterId === meterId).sort((a, b) =>
      new Date(b.readingDate) - new Date(a.readingDate)
    );
  };

  // Update reading
  const updateReading = async (id, readingData, userId = null) => {
    const oldReading = getReading(id);
    const reading = { ...oldReading, ...readingData, id };
    await storageService.saveReading(reading);
    setReadings(readings.map(r => r.id === id ? reading : r));
    
    // Enregistrer l'activité
    if (userId) {
      try {
        const changes = {};
        Object.keys(readingData).forEach(key => {
          if (oldReading[key] !== readingData[key]) {
            changes[key] = {
              before: oldReading[key],
              after: readingData[key]
            };
          }
        });
        
        await logActivity(
          ACTIVITY_TYPES.READING_UPDATED,
          userId,
          'reading',
          id,
          { changes },
          `Relevé modifié: ${reading.readingValue} m³`
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la modification du relevé:', auditError);
      }
    }
    
    return reading;
  };

  const getReading = (id) => {
    return readings.find(r => r.id === id);
  };

  // Invoice operations
  const addInvoice = async (invoiceData, userId = null) => {
    const invoice = createInvoice(invoiceData);
    await storageService.saveInvoice(invoice);
    setInvoices([...invoices, invoice]);
    
    // Enregistrer l'activité
    if (userId) {
      try {
        await logActivity(
          ACTIVITY_TYPES.BILL_CREATED,
          userId,
          'invoice',
          invoice.id,
          { invoiceData },
          `Facture créée: ${invoice.invoiceNumber || invoice.id}`
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la création de la facture:', auditError);
      }
    }
    
    return invoice;
  };

  const updateInvoice = async (id, invoiceData, userId = null) => {
    const oldInvoice = getInvoice(id);
    const invoice = { ...oldInvoice, ...invoiceData, id };
    await storageService.saveInvoice(invoice);
    setInvoices(invoices.map(i => i.id === id ? invoice : i));
    
    // Enregistrer l'activité si le statut a changé
    if (userId && oldInvoice && oldInvoice.status !== invoice.status) {
      try {
        let activityType = ACTIVITY_TYPES.BILL_UPDATED;
        let description = `Facture modifiée: ${invoice.invoiceNumber || invoice.id}`;
        
        if (invoice.status === 'issued' && oldInvoice.status !== 'issued') {
          activityType = ACTIVITY_TYPES.BILL_ISSUED;
          description = `Facture émise: ${invoice.invoiceNumber || invoice.id}`;
        } else if (invoice.status === 'cancelled') {
          activityType = ACTIVITY_TYPES.BILL_CANCELLED;
          description = `Facture annulée: ${invoice.invoiceNumber || invoice.id}`;
        }
        
        await logActivity(
          activityType,
          userId,
          'invoice',
          id,
          { statusChange: { from: oldInvoice.status, to: invoice.status } },
          description
        );
      } catch (auditError) {
        console.warn('Impossible d\'enregistrer la modification de la facture:', auditError);
      }
    }
    
    return invoice;
  };

  const getInvoice = (id) => {
    return invoices.find(i => i.id === id);
  };

  const getInvoicesBySubscriber = (subscriberId) => {
    return invoices.filter(i => i.subscriberId === subscriberId);
  };

  // Payment operations
  const addPayment = async (paymentData, userId = null) => {
    try {
      const payment = createPayment(paymentData);
      const result = await storageService.savePayment(payment);
      
      if (!result.success) {
        console.error('❌ Erreur lors de l\'enregistrement du paiement:', result.error);
        return null;
      }
      
      const savedPayment = result.data || payment;
      setPayments([...payments, savedPayment]);
      
      // Enregistrer l'activité
      if (userId) {
        try {
          await logActivity(
            ACTIVITY_TYPES.PAYMENT_CREATED,
            userId,
            'payment',
            savedPayment.id,
            { paymentData, amount: savedPayment.amount, method: savedPayment.method },
            `Paiement créé: ${savedPayment.amount} FCFA`
          );
        } catch (auditError) {
          console.warn('Impossible d\'enregistrer la création du paiement:', auditError);
        }
      }
      
      // Update invoice status
      const invoice = getInvoice(payment.invoiceId);
      if (invoice) {
        const newPaidAmount = invoice.paidAmount + payment.amount;
        const newRemainingAmount = invoice.totalAmount - newPaidAmount;
        const newStatus = newRemainingAmount <= 0 ? 'paid' : 'partially_paid';
        
        await updateInvoice(invoice.id, {
          paidAmount: newPaidAmount,
          remainingAmount: newRemainingAmount,
          status: newStatus
        }, userId);
        
        // Enregistrer si le paiement a complété la facture
        if (newStatus === 'paid' && invoice.status !== 'paid' && userId) {
          try {
            await logActivity(
              ACTIVITY_TYPES.PAYMENT_COMPLETED,
              userId,
              'payment',
              savedPayment.id,
              { totalAmount: newPaidAmount, invoiceId: invoice.id },
              `Paiement complété: ${newPaidAmount} FCFA`
            );
          } catch (auditError) {
            console.warn('Impossible d\'enregistrer le paiement complété:', auditError);
          }
        }
      }
      
      return savedPayment;
    } catch (error) {
      console.error('❌ Erreur dans addPayment:', error);
      return null;
    }
  };

  const getPaymentsByInvoice = (invoiceId) => {
    return payments.filter(p => p.invoiceId === invoiceId);
  };

  const getPaymentsBySubscriber = (subscriberId) => {
    return payments.filter(p => p.subscriberId === subscriberId);
  };

  // Statistics
  const getStatistics = () => {
    const totalSubscribers = subscribers.length;
    const totalMeters = meters.length;
    const paidInvoices = invoices.filter(i => i.status === 'paid').length;
    const unpaidInvoices = invoices.filter(i => i.status === 'overdue' || i.status === 'issued').length;
    const totalRevenue = invoices.reduce((sum, i) => sum + i.paidAmount, 0);
    const unpaidAmount = invoices.reduce((sum, i) => sum + i.remainingAmount, 0);
    const totalConsumption = readings.reduce((sum, r) => sum + r.consumption, 0);

    return {
      totalSubscribers,
      totalMeters,
      paidInvoices,
      unpaidInvoices,
      totalRevenue,
      unpaidAmount,
      totalConsumption
    };
  };

  const getUnpaidInvoices = () => {
    const now = new Date();
    return invoices.filter(i => {
      if (i.status === 'paid') return false;
      const dueDate = new Date(i.dueDate);
      const overdue = now > dueDate;
      return i.remainingAmount > 0;
    }).sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  };

  // Link reading to invoice after generation (prevent double billing)
  const updateReadingInvoiceLink = async (readingId, invoiceId) => {
    const reading = readings.find(r => r.id === readingId);
    if (reading) {
      const updatedReading = { ...reading, linkedInvoiceId: invoiceId, invoiceGenerated: true };
      await storageService.saveReading(updatedReading);
      setReadings(readings.map(r => r.id === readingId ? updatedReading : r));
    }
  };

  // Tariff operations
  const addTariff = async (tariffData) => {
    const tariff = { 
      ...tariffData, 
      id: tariffData.id || Date.now().toString() 
    };
    const result = await storageService.saveTariff(tariff);
    const savedTariff = result.success && result.data ? result.data : tariff;
    setTariffs([...tariffs, savedTariff]);
    return savedTariff;
  };

  const updateTariff = async (id, tariffData) => {
    const tariff = { ...getTariff(id), ...tariffData, id };
    const result = await storageService.saveTariff(tariff);
    const updatedTariff = result.success && result.data ? result.data : tariff;
    setTariffs(tariffs.map(t => t.id === id ? updatedTariff : t));
    return updatedTariff;
  };

  const getTariff = (id) => {
    return tariffs.find(t => t.id === id);
  };

  const deleteTariff = async (id) => {
    const result = await storageService.deleteTariff(id);
    if (result.success) {
      setTariffs(tariffs.filter(t => t.id !== id));
    }
    return result;
  };

  const value = {
    loading,
    subscribers,
    meters,
    readings,
    invoices,
    payments,
    tariffs,
    addSubscriber,
    updateSubscriber,
    getSubscriber,
    deleteSubscriber,
    addMeter,
    updateMeter,
    getMeter,
    getMetersBySubscriber,
    addReading,
    updateReading,
    getReadingsByMeter,
    addInvoice,
    updateInvoice,
    getInvoice,
    getInvoicesBySubscriber,
    addPayment,
    getPaymentsByInvoice,
    getPaymentsBySubscriber,
    getStatistics,
    getUnpaidInvoices,
    updateReadingInvoiceLink,
    addTariff,
    updateTariff,
    getTariff,
    deleteTariff
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
